import React from 'react';
import PageHero from '../components/PageHero';
import Calculator from '../components/Calculator';
import FinalCTA from '../components/FinalCTA';
import RevealOnScroll from '../components/RevealOnScroll';
import { Package, Shield, Box } from 'lucide-react';

const PricingPage: React.FC = () => {
  const services = [
    {
      category: "Package Handling & Consolidation",
      items: [
        { name: "Package Consolidation", price: "Free", unit: "per shipment", desc: "Combine multiple store packages into one box" },
        { name: "Package Storage (First 30 Days)", price: "Free", unit: "per package", desc: "Store your items while you wait for others" },
        { name: "Extended Storage (After 30 Days)", price: "$1.00", unit: "per pkg / day", desc: "Fee applied daily after the free period" },
        { name: "Package Photos (Standard)", price: "$2.00", unit: "per request", desc: "Receive 3 photos of package contents" },
        { name: "Invoice Removal", price: "Free", unit: "per package", desc: "Remove merchant invoices before shipping" },
      ]
    },
    {
      category: "Special Packaging Materials",
      items: [
        { name: "Extra Bubble Wrap", price: "$3.00", unit: "per box", desc: "Additional protection for fragile items" },
        { name: "Double Walled Box", price: "$5.00", unit: "per box", desc: "Heavy duty box for heavy or fragile items" },
        { name: "Fragile Tape & Stickers", price: "Free", unit: "per box", desc: "Marking package as fragile for carriers" },
        { name: "Security Tape", price: "$2.00", unit: "per box", desc: "Tamper-evident tape for high value items" },
        { name: "Wooden Crating", price: "Quote", unit: "per shipment", desc: "Custom wooden crate for artwork or machinery" },
      ]
    },
    {
      category: "Value Added Services",
      items: [
        { name: "Shipping Insurance", price: "$2.00", unit: "per $100 value", desc: "Cover against loss or damage in transit" },
        { name: "Return to Merchant", price: "$10.00", unit: "+ shipping", desc: "Processing fee to return items to seller" },
        { name: "Assisted Purchase", price: "5%", unit: "of total value", desc: "We buy the item for you ($10 minimum fee)" },
        { name: "Express Processing", price: "$5.00", unit: "per shipment", desc: "Jump the queue for faster dispatch" },
      ]
    }
  ];

  return (
    <>
      <PageHero 
        title="Estimate Your Shipping Cost"
        subtitle="Transparent pricing from Sri Lanka to Maldives with no hidden fees."
      />
      <Calculator />
      
      <section className="py-12 md:py-20 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3 md:mb-4">Additional Services & Fees</h2>
              <p className="text-slate-600 max-w-2xl mx-auto text-sm md:text-base">
                Customize your shipping experience with our optional add-ons. 
                Most customers stick to standard shipping, but we're here if you need extras.
              </p>
            </div>
          </RevealOnScroll>

          <div className="grid gap-8 md:gap-12">
            {services.map((section, idx) => (
              <RevealOnScroll key={idx} delay={idx * 100}>
                <div className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                  <div className="bg-brand-900 px-4 py-3 md:px-6 md:py-4">
                    <h3 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                      {idx === 0 && <Package className="w-5 h-5 text-brand-300" />}
                      {idx === 1 && <Box className="w-5 h-5 text-brand-300" />}
                      {idx === 2 && <Shield className="w-5 h-5 text-brand-300" />}
                      {section.category}
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {section.items.map((item, i) => (
                      <div key={i} className="p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-4 hover:bg-white transition-colors">
                        <div>
                          <div className="flex justify-between md:block">
                              <h4 className="font-bold text-slate-900 text-sm md:text-base">{item.name}</h4>
                              {/* Mobile only price display */}
                              <div className="text-right md:hidden">
                                  <span className="font-bold text-brand-600 text-sm">{item.price}</span>
                              </div>
                          </div>
                          <p className="text-xs md:text-sm text-slate-500 mt-1 pr-12 md:pr-0">{item.desc}</p>
                        </div>
                        {/* Desktop price display */}
                        <div className="text-right shrink-0 hidden md:block">
                          <div className="font-bold text-brand-600 text-lg">{item.price}</div>
                          <div className="text-xs text-slate-400 font-medium uppercase">{item.unit}</div>
                        </div>
                        {/* Mobile unit display */}
                        <div className="md:hidden text-right">
                           <span className="text-[10px] text-slate-400 font-medium uppercase">{item.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>

          <RevealOnScroll className="mt-12 md:mt-16 bg-blue-50 border border-blue-100 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-6">
             <div className="text-center md:text-left">
                <h4 className="font-bold text-slate-900 text-lg">Not sure what you need?</h4>
                <p className="text-slate-600 text-sm mt-1">Our support team can help you decide the best packaging options for your specific items.</p>
             </div>
             <a href="/contact" className="w-full md:w-auto text-center bg-white text-brand-600 border border-brand-200 font-bold px-6 py-3 rounded-lg hover:bg-brand-50 transition shadow-sm whitespace-nowrap">
                Contact Support
             </a>
          </RevealOnScroll>

        </div>
      </section>

      <FinalCTA />
    </>
  );
};

export default PricingPage;